/**
 * Daily Rate Baseline (Punjab × 6 commodities).
 *
 * The senior 15-year-experience admin posts the morning baseline once a
 * day. The platform's market intelligence layer reads from this collection
 * as the authoritative "today rate" and the seed for tomorrow's prediction.
 * A midnight-PKT cron auto-rolls the live tomorrow into the next day so the
 * market keeps moving without admin intervention. Admin can override at any
 * time — saving immediately invalidates the AI insight cache and emits a
 * RATE_UPDATE feed event (no admin attribution; users see a neutral rate
 * change).
 *
 * UX:
 *   - Three industry cards (Egg / Broiler / Feed Material), two products each.
 *   - Each row: Today Rate (PKR), Tomorrow Prediction (PKR, optional —
 *     defaults to Today), Confidence (0-100, default 20), Notes (optional),
 *     Source badge (ADMIN / AUTO_ROLLED / EMPTY).
 *   - On mount: GET /daily and prefill every row from whatever's stored.
 *   - Single "Save Today's Baselines" bulk POST at the bottom.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Form,
  Input,
  InputNumber,
  Button,
  Tabs,
  Tag,
  Typography,
  Row,
  Col,
  message,
  Alert,
  Grid,
  Empty,
  Table,
  Tooltip,
  Slider,
  Select,
} from "antd";
import { InfoCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import { connect } from "react-redux";
import moment from "moment";
import {
  getAdminBaselines,
  saveAdminBaselines,
  getAdminBaselineHistory,
} from "../../RestServices/postingService";

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { useBreakpoint } = Grid;

// Commodities in scope for v1 — Punjab only.
// No per-product min/max bounds: the admin is a 15-year industry expert and
// is the source of truth. Whatever they enter is what we save.
const INDUSTRIES = [
  {
    value: "EGG_INDUSTRY",
    label: "Egg Industry",
    products: [
      { value: "WHITE_EGG", label: "White Egg" },
      { value: "WHITE_LAYER", label: "White Layer" },
    ],
  },
  {
    value: "BROILER_INDUSTRY",
    label: "Broiler Industry",
    products: [
      { value: "BROILER", label: "Broiler" },
      { value: "BROILER_CHICK", label: "Broiler Chick" },
    ],
  },
  {
    value: "FEED_MATERIAL",
    label: "Feed Material",
    products: [
      { value: "CORN", label: "Corn" },
      { value: "SOYABEAN", label: "Soyabean" },
    ],
  },
];

const PROVINCE = "punjab"; // v1: Punjab only

function sourceTag(source) {
  if (source === "ADMIN") return <Tag color="gold">ADMIN</Tag>;
  if (source === "AUTO_ROLLED") return <Tag color="blue">AUTO-ROLLED</Tag>;
  return <Tag>—</Tag>;
}

function todayPktKey() {
  // YYYY-MM-DD in Asia/Karachi (UTC+5, no DST). Mirrors backend convention.
  const d = new Date(Date.now() + 5 * 60 * 60 * 1000);
  return d.toISOString().slice(0, 10);
}

function DailyRateBaselinePage({ Auth }) {
  const token = Auth?.user?.token;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  // Per-industry saving state: holds the industry value while its save is in
  // flight (so only that card's button shows a spinner). null = idle.
  const [savingIndustry, setSavingIndustry] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [storedRows, setStoredRows] = useState([]); // GET /daily response
  const [liveRows, setLiveRows] = useState([]); // GET /daily.live (current forecast view)
  const [history, setHistory] = useState([]);
  const [historyDays, setHistoryDays] = useState(14);
  const [setAt, setSetAt] = useState(null);
  const [setBy, setSetBy] = useState(null);
  const [dateKey] = useState(todayPktKey());
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // Flatten the industry → products list once for table iteration.
  const allRows = useMemo(() => {
    const out = [];
    INDUSTRIES.forEach((ind) => {
      ind.products.forEach((p) => {
        out.push({
          industry: ind.value,
          industryLabel: ind.label,
          product: p.value,
          productLabel: p.label,
        });
      });
    });
    return out;
  }, []);

  // ── Build a lookup from `(industry, product)` → stored row so we can prefill. ──
  const storedByKey = useMemo(() => {
    const map = {};
    storedRows.forEach((r) => {
      map[`${r.industry}__${r.product}`] = r;
    });
    return map;
  }, [storedRows]);

  const liveByKey = useMemo(() => {
    const map = {};
    liveRows.forEach((r) => {
      map[`${r.industry}__${r.product}`] = r;
    });
    return map;
  }, [liveRows]);

  // ── Initial load: fetch today's baselines and prefill the form ──
  const loadDaily = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await getAdminBaselines({ province: PROVINCE, dateKey }, token);
      // Backend shape: { baselines: [{ ...row, live: {...} }, ...] }
      // Each enriched row carries its own `.live` block computed from today's
      // votes + signal delta. Strip and split for the table-style render.
      const baselines = data?.baselines || data?.rows || [];
      const rows = baselines.map(({ live, ...rest }) => rest);
      const live = baselines
        .filter((b) => b.live)
        .map((b) => ({ industry: b.industry, product: b.product, ...b.live }));
      setStoredRows(rows);
      setLiveRows(live);

      // Pick the most recent setAt + setBy for the banner (if any row is admin-set).
      const adminRow = rows.find((r) => r.source === "ADMIN");
      const anyRow = adminRow || rows[0];
      setSetAt(anyRow?.setAt || null);
      setSetBy(anyRow?.setBy || null);

      // Push values into the form. Every commodity gets its own group of
      // four fields — keyed by `<industry>__<product>` so they don't collide.
      const fieldValues = {};
      allRows.forEach((row) => {
        const key = `${row.industry}__${row.product}`;
        const stored = rows.find(
          (r) => r.industry === row.industry && r.product === row.product
        );
        fieldValues[`${key}__todayRate`] = stored?.todayRate ?? null;
        fieldValues[`${key}__tomorrowPrediction`] =
          stored?.tomorrowPrediction ?? null;
        fieldValues[`${key}__confidence`] =
          stored?.confidence != null ? stored.confidence : 20;
        fieldValues[`${key}__notes`] = stored?.notes || "";
        if (row.product === "BROILER") {
          fieldValues[`${key}__retailPrice`] = stored?.retailPrice ?? (stored?.todayRate ? Math.round(stored.todayRate + 175) : null);
          fieldValues[`${key}__wholeCuts`] = stored?.wholeCuts ?? (stored?.todayRate ? Math.round(stored.todayRate * 1.34) : null);
        }
      });
      // AI insight inputs moved to the Rate Correction page — no longer
      // prefilled here.
      form.setFieldsValue(fieldValues);
    } catch (e) {
      message.error(
        e?.response?.data?.message || "Failed to fetch today's baselines"
      );
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    if (!token) return;
    setHistoryLoading(true);
    try {
      const data = await getAdminBaselineHistory(
        { province: PROVINCE, days: historyDays },
        token
      );
      setHistory(data?.rows || []);
    } catch (e) {
      message.error(
        e?.response?.data?.message || "Failed to fetch baseline history"
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    loadDaily();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyDays]);

  // ── Per-row reset (clears that row's form inputs only). ──
  // The actual delete happens on Save — this just blanks the inputs.
  const resetRow = (row) => {
    const key = `${row.industry}__${row.product}`;
    const resetObj = {
      [`${key}__todayRate`]: null,
      [`${key}__tomorrowPrediction`]: null,
      [`${key}__confidence`]: 20,
      [`${key}__notes`]: "",
    };
    if (row.product === "BROILER") {
      resetObj[`${key}__retailPrice`] = null;
      resetObj[`${key}__wholeCuts`] = null;
    }
    form.setFieldsValue(resetObj);
  };

  // ── Per-industry save handler. ──
  // Each industry card has its own Save button. The handler collects only
  // the rows belonging to that industry, skips any with a blank Today Rate
  // (admin can save partial sheets — e.g. just BROILER without forcing CORN),
  // and POSTs as a single request. Saves are independent so admin can iterate
  // industry by industry without losing in-progress edits in another card.
  const onSaveIndustry = async (industryValue) => {
    if (!token) return;
    const values = form.getFieldsValue();
    const industryRows = allRows.filter((r) => r.industry === industryValue);

    const baselines = [];
    industryRows.forEach((row) => {
      const key = `${row.industry}__${row.product}`;
      const todayRate = values[`${key}__todayRate`];
      if (todayRate == null || todayRate === "") return; // skip blank rows
      const tomorrowPrediction = values[`${key}__tomorrowPrediction`];
      const confidence = values[`${key}__confidence`];
      const notes = values[`${key}__notes`];
      const baselineRow = {
        industry: row.industry,
        product: row.product,
        todayRate: Number(todayRate),
        tomorrowPrediction:
          tomorrowPrediction != null && tomorrowPrediction !== ""
            ? Number(tomorrowPrediction)
            : Number(todayRate),
        confidence: confidence != null ? Number(confidence) : 20,
        notes: notes || null,
      };
      if (row.product === "BROILER") {
        const retailPrice = values[`${key}__retailPrice`];
        const wholeCuts = values[`${key}__wholeCuts`];
        baselineRow.retailPrice = retailPrice != null && retailPrice !== "" ? Number(retailPrice) : Math.round(todayRate + 175);
        baselineRow.wholeCuts = wholeCuts != null && wholeCuts !== "" ? Number(wholeCuts) : Math.round(todayRate * 1.34);
      }
      baselines.push(baselineRow);
    });

    if (baselines.length === 0) {
      message.warning(
        "Nothing to save — fill at least one Today Rate in this industry."
      );
      return;
    }

    setSavingIndustry(industryValue);
    try {
      const resp = await saveAdminBaselines(
        { province: PROVINCE, dateKey, baselines },
        token
      );
      const written =
        (resp?.rows?.length) || (resp?.baselines?.length) || baselines.length;
      const indLabel =
        INDUSTRIES.find((i) => i.value === industryValue)?.label ||
        industryValue;
      message.success(
        `Saved ${written} ${indLabel} baseline${written === 1 ? "" : "s"}. AI insight refreshed; feed updated.`
      );
      // Re-fetch so the form picks up the freshly-stored values + setAt/setBy.
      loadDaily();
      loadHistory();
    } catch (e) {
      message.error(
        e?.response?.data?.message ||
          "Failed to save baselines. Check your inputs and permissions."
      );
    } finally {
      setSavingIndustry(null);
    }
  };

  // ── Per-row card body ────────────────────────────────────────────────────
  const renderRow = (row) => {
    const key = `${row.industry}__${row.product}`;
    const stored = storedByKey[key];
    const live = liveByKey[key];

    return (
      <div
        key={key}
        style={{
          padding: "14px 0",
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <Row gutter={[12, 12]} align="middle">
          {/* Commodity name + source badge */}
          <Col xs={24} md={5}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>
              {row.productLabel}
            </div>
            <div style={{ marginTop: 4, display: "flex", gap: 6, alignItems: "center" }}>
              {sourceTag(stored?.source)}
              {stored?.setAt && (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {moment(stored.setAt).format("HH:mm")}
                </Text>
              )}
            </div>
          </Col>

          {/* Today Rate (required for save) */}
          <Col xs={12} md={4}>
            <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 2 }}>
              Today (PKR)
            </div>
            <Form.Item
              name={`${key}__todayRate`}
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                style={{ width: "100%" }}
                step={0.5}
                placeholder="e.g. 280"
              />
            </Form.Item>
          </Col>

          {/* Tomorrow Prediction (defaults to today on save) */}
          <Col xs={12} md={4}>
            <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 2 }}>
              Tomorrow (PKR)
            </div>
            <Form.Item
              name={`${key}__tomorrowPrediction`}
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                style={{ width: "100%" }}
                step={0.5}
                placeholder="defaults to Today"
              />
            </Form.Item>
          </Col>

          {/* Confidence — slider mirrors the 0-100 scale used in the model. */}
          <Col xs={24} md={5}>
            <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 2 }}>
              Confidence (0–100)
            </div>
            <Form.Item
              name={`${key}__confidence`}
              style={{ marginBottom: 0 }}
              initialValue={20}
            >
              <ConfidenceField />
            </Form.Item>
          </Col>

          {/* Live tomorrow (read-only) — what the system would predict
              right now given today's baseline + today's signal + trend bias.
              Helps admin decide whether to intervene. */}
          <Col xs={12} md={3}>
            <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 2 }}>
              Live tomorrow
            </div>
            <div
              style={{
                fontWeight: 600,
                color: live?.tomorrowMid != null ? "#D45216" : "#bfbfbf",
              }}
            >
              {live?.tomorrowMid != null ? `Rs. ${live.tomorrowMid}` : "—"}
            </div>
          </Col>

          <Col xs={12} md={3} style={{ textAlign: "right" }}>
            <Button size="small" onClick={() => resetRow(row)}>
              Reset
            </Button>
          </Col>

          {row.product === "BROILER" && (
            <Col xs={24} style={{ marginTop: 8, marginBottom: 8 }}>
              <Row gutter={[12, 12]}>
                <Col xs={12} md={12}>
                  <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 2 }}>
                    Retail Price (Today, PKR)
                  </div>
                  <Form.Item
                    name={`${key}__retailPrice`}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      step={0.5}
                      placeholder="e.g. 680 (derived: FarmGate + 175)"
                    />
                  </Form.Item>
                </Col>
                <Col xs={12} md={12}>
                  <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 2 }}>
                    Whole Broiler Cuts (Today, PKR)
                  </div>
                  <Form.Item
                    name={`${key}__wholeCuts`}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      step={0.5}
                      placeholder="e.g. 670 (derived: FarmGate * 1.34)"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          )}

          {/* Notes — admin commentary. Persisted on the row, not surfaced
              with admin attribution to users (silent override rules from
              the privacy chapter still apply). */}
          <Col xs={24}>
            <Form.Item name={`${key}__notes`} style={{ marginBottom: 0, marginTop: 4 }}>
              <Input.TextArea
                rows={1}
                maxLength={300}
                placeholder="Optional notes — internal commentary"
              />
            </Form.Item>
          </Col>
        </Row>
      </div>
    );
  };

  // ── History columns ──────────────────────────────────────────────────────
  const historyColumns = [
    {
      title: "Date",
      dataIndex: "dateKey",
      width: 110,
      render: (v) => <Text strong>{v}</Text>,
    },
    {
      title: "Industry",
      dataIndex: "industry",
      width: 150,
      render: (v) => (v || "").replace(/_/g, " "),
    },
    {
      title: "Product",
      dataIndex: "product",
      width: 150,
      render: (v) => (v || "").replace(/_/g, " "),
    },
    {
      title: "Today",
      dataIndex: "todayRate",
      width: 100,
      align: "right",
      render: (v) => (v == null ? "—" : `Rs. ${v}`),
    },
    {
      title: "Retail Price",
      dataIndex: "retailPrice",
      width: 110,
      align: "right",
      render: (v) => (v == null ? "—" : `Rs. ${v}`),
    },
    {
      title: "Whole Cuts",
      dataIndex: "wholeCuts",
      width: 110,
      align: "right",
      render: (v) => (v == null ? "—" : `Rs. ${v}`),
    },
    {
      title: "Tomorrow",
      dataIndex: "tomorrowPrediction",
      width: 110,
      align: "right",
      render: (v) => (v == null ? "—" : `Rs. ${v}`),
    },
    {
      title: "Confidence",
      dataIndex: "confidence",
      width: 110,
      align: "right",
      render: (v) => (v == null ? "—" : `${v}/100`),
    },
    { title: "Source", dataIndex: "source", width: 120, render: sourceTag },
    {
      title: "By",
      dataIndex: "setBy",
      width: 110,
      render: (v) => v || "—",
    },
    {
      title: "Notes",
      dataIndex: "notes",
      render: (v) =>
        v ? (
          <Tooltip title={v} placement="topLeft">
            <div
              style={{
                maxWidth: 240,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {v}
            </div>
          </Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
  ];

  // ── Banner: when did admin / cron last touch this day? ──
  const banner = useMemo(() => {
    if (!setAt) {
      return {
        type: "warning",
        message: "No baselines saved for today yet — fill the form below and hit Save.",
      };
    }
    const ts = moment(setAt).format("D MMM YYYY · HH:mm");
    if (storedRows.every((r) => r.source === "AUTO_ROLLED")) {
      return {
        type: "info",
        message: `Auto-rolled at ${ts}. Admin can override any value before saving.`,
      };
    }
    return {
      type: "success",
      message: `Last set at ${ts}${setBy ? ` by ${setBy}` : ""}.`,
    };
  }, [setAt, setBy, storedRows]);

  return (
    <div style={{ padding: isMobile ? 12 : 24, maxWidth: 1400, margin: "0 auto" }}>
      <div style={{ marginBottom: 16 }}>
        <Title level={isMobile ? 4 : 3} style={{ color: "#fff", margin: 0 }}>
          Daily Rate Baseline · Punjab
        </Title>
        <Text style={{ color: "#ccc", fontSize: isMobile ? 12 : 14 }}>
          Set today&rsquo;s morning rate for each commodity. The platform reads
          these as the source of truth for &ldquo;today&rsquo;s rate&rdquo; and
          the seed for tomorrow&rsquo;s prediction. Saving immediately refreshes
          the AI insight and emits a rate-update feed event.
        </Text>
      </div>

      <Alert
        showIcon
        type={banner.type}
        message={banner.message}
        style={{ marginBottom: 16, borderRadius: 10 }}
        action={
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={loadDaily}
            loading={loading}
          >
            Refresh
          </Button>
        }
      />

      <Tabs defaultActiveKey="set" size={isMobile ? "small" : "middle"}>
        {/* ── SET TODAY'S BASELINES ─────────────────────────────────────── */}
        <TabPane tab="Set Today's Baselines" key="set">
          <Form 
            form={form} 
            requiredMark="optional"
            onValuesChange={(changedValues) => {
              const broilerTodayKey = "BROILER_INDUSTRY__BROILER__todayRate";
              if (changedValues[broilerTodayKey] !== undefined) {
                const todayRate = changedValues[broilerTodayKey];
                if (todayRate != null && todayRate > 0) {
                  form.setFieldsValue({
                    "BROILER_INDUSTRY__BROILER__retailPrice": Math.round(todayRate + 175),
                    "BROILER_INDUSTRY__BROILER__wholeCuts": Math.round(todayRate * 1.34)
                  });
                }
              }
            }}
          >
            {INDUSTRIES.map((ind) => (
              <Card
                key={ind.value}
                title={
                  <span style={{ fontWeight: 600 }}>
                    {ind.label}{" "}
                    <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                      · {ind.products.length} commodities
                    </Text>
                  </span>
                }
                // Per-industry Save lives in the card header so admin works
                // industry-by-industry: edit egg rows → Save Egg, then move to
                // broiler. Saves are independent so an in-progress edit in
                // another card is never lost.
                extra={
                  <Button
                    type="primary"
                    size={isMobile ? "small" : "middle"}
                    onClick={() => onSaveIndustry(ind.value)}
                    loading={savingIndustry === ind.value}
                    disabled={savingIndustry && savingIndustry !== ind.value}
                    style={{
                      background: "#D45216",
                      borderColor: "#D45216",
                      fontWeight: 600,
                    }}
                  >
                    Save {ind.label}
                  </Button>
                }
                bordered={false}
                style={{
                  marginBottom: 16,
                  borderRadius: 14,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                }}
                bodyStyle={{ padding: isMobile ? 12 : 20 }}
              >
                {allRows
                  .filter((r) => r.industry === ind.value)
                  .map(renderRow)}

                {/* AI Insight (English / Urdu) intentionally lives on the
                    Rate Correction page now — admin sets the day's rate
                    here and writes the user-facing narrative there at the
                    moment of correction. Keeps each page's job focused. */}

                {/* Card-bottom save for mobile / long screens — duplicates
                    the header button so admin doesn't have to scroll up. */}
                <div
                  style={{
                    marginTop: 12,
                    paddingTop: 12,
                    borderTop: "1px solid #f0f0f0",
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    type="primary"
                    size="large"
                    onClick={() => onSaveIndustry(ind.value)}
                    loading={savingIndustry === ind.value}
                    disabled={savingIndustry && savingIndustry !== ind.value}
                    block={isMobile}
                    style={{
                      background: "#D45216",
                      borderColor: "#D45216",
                      fontWeight: 600,
                      minWidth: isMobile ? undefined : 220,
                    }}
                  >
                    Save {ind.label}
                  </Button>
                </div>
              </Card>
            ))}
          </Form>
        </TabPane>

        {/* ── HISTORY ────────────────────────────────────────────────────── */}
        <TabPane tab="History" key="history">
          <Card
            bordered={false}
            style={{
              borderRadius: 14,
              boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
            }}
            bodyStyle={{ padding: isMobile ? 12 : 20 }}
          >
            <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 16 }}>
              <Col xs={24} sm={12} md={6}>
                <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>
                  Window
                </div>
                <Select
                  value={historyDays}
                  onChange={setHistoryDays}
                  size="large"
                  style={{ width: "100%" }}
                >
                  <Select.Option value={7}>Last 7 days</Select.Option>
                  <Select.Option value={14}>Last 14 days</Select.Option>
                  <Select.Option value={30}>Last 30 days</Select.Option>
                  <Select.Option value={90}>Last 90 days</Select.Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Button
                  onClick={loadHistory}
                  loading={historyLoading}
                  size="large"
                  block
                  style={{ marginTop: isMobile ? 0 : 18 }}
                >
                  Refresh
                </Button>
              </Col>
              <Col xs={24} md={12} style={{ textAlign: isMobile ? "left" : "right" }}>
                <Text type="secondary">
                  {history.length} row{history.length === 1 ? "" : "s"} shown
                </Text>
              </Col>
            </Row>

            <Table
              rowKey={(r) => r._id || `${r.dateKey}__${r.industry}__${r.product}`}
              dataSource={history}
              columns={historyColumns}
              loading={historyLoading}
              pagination={{ pageSize: 20, showSizeChanger: false }}
              scroll={{ x: 1100 }}
              size="middle"
              locale={{ emptyText: <Empty description="No baselines yet" /> }}
            />
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
}

// ── Confidence input — slider + numeric input synced. ──
// Lifted into its own component so AntD's Form sees a single value field
// (the slider/input both call onChange with the same scalar).
function ConfidenceField({ value = 20, onChange }) {
  const v = value == null ? 20 : value;
  return (
    <Row gutter={8} align="middle">
      <Col flex="auto">
        <Slider
          value={v}
          onChange={onChange}
          min={0}
          max={100}
          tooltip={{ formatter: (val) => `${val}/100` }}
        />
      </Col>
      <Col>
        <InputNumber
          value={v}
          onChange={onChange}
          min={0}
          max={100}
          style={{ width: 70 }}
        />
      </Col>
    </Row>
  );
}

const mapStateToProps = (store) => ({
  Auth: store.Authentication,
});

export default connect(mapStateToProps)(DailyRateBaselinePage);
